import { observable, computed } from 'mobx';
import { EventEmitter2 } from 'eventemitter2';

export const uppercaseFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);

export const cleanName = (name) => {
  if (name.startsWith('_')) {
      name = name.slice(1);
  }

  if (name.startsWith('0x')) {
      name = 'zzzz' + name; // heh
  }

  return name;
};

export const makeNative = (name) => {
  return uppercaseFirst(name.toLowerCase().replace('0x', 'n_0x').replace(/_([a-z])/g, (regs) => regs[1].toUpperCase()));
};

export const renderName = (native) => native.name || native.hash;

export const cleanNs = (ns) => ns === 'CFX' ? '___CFX' : ns;

export const makeList = (natives, filterSet) => {
  return Object.entries(natives)
    .sort((a, b) => cleanNs(a[0]).localeCompare(cleanNs(b[0])))
    .map(([ namespace, natives ]) => ({
      namespace,
      natives: Object
        .entries(natives)
        .filter(([hash]) => !filterSet || filterSet.has(hash))
        .map(([hash, data]) => ({
          hash,
          ...data,
          type: 'native',
          name: data.name.startsWith('_0x') ? data.name.substr(1, -1) : data.name,
          results: data.return_type ?? data.results,
          description: data.comment ? '```\n' + data.comment+ '\n```' : data.description
        }))
        .sort((a, b) => cleanName(renderName(a)).localeCompare(cleanName(renderName(b))))
    }))
    .filter(a => a.natives.length !== 0);
};

export const flattenList = (inNatives) => {
  const returnList = [];

  for (const { namespace, natives } of inNatives) {
      returnList.push({ type: 'namespace', name: namespace });

      for (const native of natives) {
        returnList.push({
          ...native,
          namespace
        });
      }
  }

  return returnList;
};

RegExp.quote = function(str) {
  return (str+'').replace(/[.?*+^$[\]\\(){}|-]/g, "\\$&");
};

export class ViewNativeList {
  @observable rawNatives = [];
  @observable filter = '';

  @observable apiset = 'all';
  @observable language = window.localStorage.getItem('nativeRefLanguage') || 'c';

  setNatives(natives) {
    this.rawNatives = natives.map(a => new ViewNative(a));
  }

  findNative(hash) {
    return this.rawNatives.find(a => a.native.hash === hash);
  }

  findNamespace(ns) {
    return this.rawNatives.find(a => a.native.type === 'namespace' && a.native.name === ns.substr(2))
  }

  @computed get natives() {
    const filterBit = RegExp.quote(this.filter);
    const re = new RegExp(filterBit, 'i');

    const tempList = this.rawNatives.filter(a => {
      if (a.native.type === 'namespace') {
        return true;
      }

      if (this.apiset !== 'all') {
        if (a.native.apiset !== 'shared' && a.native.apiset !== this.apiset) {
          if (this.apiset !== 'client' || a.native.apiset) {
            return false;
          }
        }
      }

      if (filterBit.length === 0) {
        return true;
      }

      if (a.native.name) {
        if (a.native.name.match(re)) {
          return true;
        }

        if (makeNative(a.native.name).match(re)) {
          return true;
        }
      }

      if (a.native.hash) {
        if (a.native.hash.match(re)) {
          return true;
        }
      }

      for (const alias of a.native.aliases || []) {
        if (alias.match(re)) {
          return true;
        }

        if (makeNative(alias).match(re)) {
          return true;
        }
      }

      if (a.native.description) {
        if (a.native.description.match(re)) {
          return true;
        }
      }

      return false;
    });

    const nsList = {};

    for (const entry of tempList) {
      if (entry.native.type === 'native') {
        if (!nsList[entry.native.namespace]) {
          nsList[entry.native.namespace] = 0;
        }

        nsList[entry.native.namespace]++;
      }
    }

    return tempList.filter(a => {
      if (a.native.type !== 'namespace') {
        return true;
      }

      if (nsList[a.native.name] > 0) {
        return true;
      }

      return false;
    });
  }
}

export class ViewNative {
  native = {};
  @observable active = false;

  constructor(native) {
    this.native = native;
  }
}

export class ViewSelection {
  @observable curNative;
  @observable curIndex = undefined;
  @observable initialScroll = false;
}

export class ViewApp extends EventEmitter2 {
  @observable viewNativeList;
  @observable selection;

  constructor() {
    super();

    this.viewNativeList = new ViewNativeList();
    this.selection = new ViewSelection();

    const promises = Promise.all([
      window.fetch('https://raw.githubusercontent.com/alloc8or/rdr3-nativedb-data/master/natives.json')
        .then(data => data.json())
        .catch(() => window.reload()),
      window.fetch('https://runtime.fivem.net/doc/natives_cfx.json')
        .then(data => data.json())
        .catch(() => window.reload()),
    ]);

    promises
      .then(([game, cfx]) => this.setNatives(flattenList([...makeList(cfx), ...makeList(game)])))
      .then(() => this.emit('loaded-natives'));
  }

  setNatives(natives) {
    this.viewNativeList.setNatives(natives);
  }

  findNative(hash) {
    return this.viewNativeList.findNative(hash);
  }

  findNamespace(ns) {
    return this.viewNativeList.findNamespace(ns);
  }
}

export const appStore = new ViewApp();
